import type { StreamType } from 'chromecast-caf-receiver/cast.framework.messages';
import { ACTIONS, DETAILED_ERROR_CODES, NAMESPACE_TALPA, NAMESPACE_TWC } from '../../constants';
import {
  createQueryString,
  getAppConfiguration,
  setLicenseRequestBody,
  getClientIP,
  getDeviceTier,
  getDeviceCapabilities,
} from '../../utils';
import getToken from '../../utils/getToken';
import getVideo from '../../utils/getVideo';
import { customInteraction, formatTime, noticeError, setAdditionalAttributes } from '../../utils/newrelic';
import probeDrmSupport from '../../utils/probeDrmSupport';
import { DeviceAuthManager } from '../../utils/deviceAuth';
import { isVideoCompleted } from '../../utils/video';
import Logger from './Logger';
import getPvxDrmToken from '../../utils/getPvxDrmToken';
import getPlaySessionId from '../../utils/getPlaySessionId';

declare global {
  interface Window {
    isProduction: boolean;
    utag: {
      data: {
        tealium_visitor_id: string;
        tealium_profile: string;
        hashed_visitor_id?: string;
        app_brandname: string;
        app_name: string;
      };
    };
  }
}
export default class BasePlayer {
  contentId?: string;
  playerChannel?: string;
  platform?: string;
  context: framework.CastReceiverContext;
  currentContentTime?: number;
  currentDuration: number;
  currentMediaTime?: number;
  currentTrack?: string;
  videoApiURL = '';
  logger: Logger;
  playerManager: framework.PlayerManager;
  textTracksManager: framework.TextTracksManager;
  request?: messages.LoadRequestData;
  textTracks: Track[] = [];
  uid?: string;
  videoElement: HTMLVideoElement;
  sendAdUpdates = false;
  streamStarted = false;
  adPlaying = false;
  video = {
    seeking: false,
    volume: false,
    position: 0,
  };
  videoMetadata: Metadata = {};
  currentAd: {
    adId?: string;
    creativeId?: string;
    contentUrl?: string;
    duration?: number;
    sequence?: number;
    podcount?: number;
    vastMediaFiles?: string;
  } | null = null;
  creatives: {
    breakId: string;
    sequence: string;
    adId: string;
    creativeId: string;
    clickThroughUrl?: string;
    mediaFiles: string[];
    vastMediaFiles: string;
  }[] = [];
  loadRequest?: framework.messages.LoadRequestData;
  isRecovery = false;
  failedAd = false;
  failedAdReason?: string;
  failedShaka = false;
  failedShakaCode?: number;
  deviceAuthManager: DeviceAuthManager | null = null;
  authPromise: Promise<void> | null = null;
  authenticatedUid?: string;

  constructor() {
    this.context = cast.framework.CastReceiverContext.getInstance();
    this.logger = new Logger(this.context);
    this.playerManager = this.context.getPlayerManager();
    this.textTracksManager = this.playerManager.getTextTracksManager();
    this.currentDuration = this.playerManager.getDurationSec();
    this.videoElement = (document.getElementById('player') as any).getMediaElement();
    const options = new cast.framework.CastReceiverOptions();

    options.customNamespaces = {
      [NAMESPACE_TWC]: cast.framework.system.MessageType.STRING,
      [NAMESPACE_TALPA]: cast.framework.system.MessageType.STRING,
    };
    this.context.start(options);

    this.setupCallbacks();

    getClientIP().then((clientIP) => {
      setAdditionalAttributes({
        clientIP,
      });
    });

    probeDrmSupport().then((result) => {
      customInteraction('DRM Support Probe', {
        widevine_hw_secure_all: result['HW_SECURE_ALL'] as boolean,
        widevine_hw_secure_decode: result['HW_SECURE_DECODE'] as boolean,
        widevine_hw_secure_crypto: result['HW_SECURE_CRYPTO'] as boolean,
        widevine_sw_secure_decode: result['SW_SECURE_DECODE'] as boolean,
        widevine_sw_secure_crypto: result['SW_SECURE_CRYPTO'] as boolean,
        widevine_basic: result['basic'] as boolean,
        widevine_highest_level: (result.highestLevel ?? 'none') as string,
      });
      setAdditionalAttributes({
        widevine_highest_level: result.highestLevel ?? 'none',
      });
    });
  }

  initMonitoring() {}

  setupCallbacks() {
    // Receives messages from sender app. The message is a comma separated string
    // where the first substring indicates the function to be called and the
    // following substrings are the parameters to be passed to the function.
    this.context.addCustomMessageListener(NAMESPACE_TWC, (event) => {
      const message = event.data.split(',');
      const method = message[0];
      switch (method) {
        case 'requestAd':
          // Todo: check this action with app team
          // const adTag = message[1];
          // const currentTime = parseFloat(message[2]);
          // this.requestAd_(adTag, currentTime);
          this.broadcast('requestAd currently not implemented');
          break;
        case 'seek':
          const time = parseFloat(message[1]);
          this.seek_(time);
          break;
        default:
          this.broadcast('Message not recognized');
          break;
      }
    });

    this.context.addCustomMessageListener(NAMESPACE_TALPA, (event) => {
      const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      const { type, value } = data;
      if (type === ACTIONS.SET_ACTIVE_TRACK) {
        this.currentTrack = value;
        this.setActiveCaption(value);
      }
    });

    this.playerManager.setMessageInterceptor(cast.framework.messages.MessageType.LOAD, async (loadRequestData) => {
      this.loadRequest = loadRequestData;
      const { contentId, contentUrl, customData, vmapAdsRequest } = loadRequestData.media;

      let env = 'prd';

      if (!window.isProduction) {
        const [, matchedEnv] = contentUrl?.match(/^https:\/\/vod(?:.*)\.(acc|tst)/) || [];
        env = matchedEnv || 'prd';
      }
      this.videoApiURL =
        customData?.videoApiEndpoint?.replace('/graphql', '') || `https://api.${env}.video.talpa.network`;

      // Prevent ads on restart
      const breakManager = this.playerManager.getBreakManager();
      if (customData?.preventAds) {
        this.logger.info('preventAds with setBreakClipLoadInterceptor');
      }
      const interceptor = customData?.preventAds ? () => null : null;
      breakManager.setBreakClipLoadInterceptor(interceptor);

      const senders = this.context.getSenders();
      const senderUserAgent = senders[0]?.userAgent;
      const allSenderUserAgents = senders.map((s) => s.userAgent).join('; ');

      if (customData?.playerChannel) {
        this.playerChannel = customData.playerChannel;
      }

      if (customData?.platform) {
        this.platform = customData.platform;
      }

      if (!this.playerChannel) {
        const appConfig = getAppConfiguration(vmapAdsRequest?.adTagUrl, allSenderUserAgents);
        this.playerChannel = appConfig?.playerChannel ?? 'Unknown';
        if (!this.platform && appConfig?.platform) {
          this.platform = appConfig.platform;
        }
      }

      setAdditionalAttributes({
        media_id: contentId,
        userAgent: navigator.userAgent,
        device_tier: getDeviceTier(),
        device_capabilities: getDeviceCapabilities().join(', '),
        device_resolution: `${window.innerWidth}x${window.innerHeight}`,
        initial_load_request_data: JSON.stringify(loadRequestData.media),
        player_channel: this.platform ? `${this.playerChannel} ${this.platform}` : this.playerChannel,
        media_offset: formatTime(loadRequestData.currentTime ?? 0),
        sender_user_agent: senderUserAgent,
        all_sender_user_agents: allSenderUserAgents,
      });

      if (customData?.initiateAuth) {
        await this.startDeviceAuth(customData.uid);
      }

      const video = await getVideo({
        guid: contentId,
        videoApiURL: this.videoApiURL,
        contentUrl,
        accessToken: this.getDeviceAccessToken(),
      });

      this.videoMetadata = video?.metadata || {};

      if (video?.source) {
        loadRequestData.media.contentUrl = video.source.file;
        loadRequestData.media.contentType = video.source.type;
        setAdditionalAttributes({
          source_type: video?.source?.type,
          drm_type: video.source.drm && cast.framework.ContentProtection.WIDEVINE,
          is_pvx: video.isPvx,
          media_videotype: video.type,
        });

        loadRequestData.media.customData = {
          ...loadRequestData.media.customData,
          video,
        };
        if (video.isPvx) {
          if (video.metadata.media_videotype === 'live') loadRequestData.media.streamType = 'LIVE' as StreamType;
          loadRequestData.media.vmapAdsRequest = undefined;
        }
      } else {
        // Clear ad config when video sources can't be fetched
        loadRequestData.media.vmapAdsRequest = undefined;
      }

      setAdditionalAttributes({
        load_request_data: JSON.stringify({
          ...loadRequestData.media,
          customData: {
            ...loadRequestData.media.customData,
            video: loadRequestData.media.customData?.video ? '[video_object_present]' : undefined,
          },
        }),
      });

      if (customData?.freewheelConfig && video && !video.isPvx) {
        const { globalParams, keyValues } = customData.freewheelConfig;

        const globalParameters = {
          ...globalParams,
          nw: '506166',
          prof: '506166:talpa_embedded3p_chromecast',
          resp: 'vmap1',
          caid: contentId,
          pvrn: Math.floor(Math.random() * 1e10), // random number
          vprn: Math.floor(Math.random() * 1e10), // random number
          flag: '+play+scpv+sltp+emcr+slcb+vicb+fbad+nucr+aeti',
          metr: '47',
        };

        const keyValueParameters = {
          ...keyValues,
          _fw_h_user_agent: navigator.userAgent,
        };

        loadRequestData.media.vmapAdsRequest = {
          adTagUrl: `https://7b936.v.fwmrm.net/ad/g/1?${createQueryString(globalParameters)};${createQueryString(
            keyValueParameters,
          )};`,
        };
      }

      customInteraction('Load request');

      this.initMonitoring();

      return loadRequestData;
    });

    this.playerManager.setMediaPlaybackInfoHandler(async (loadRequest, playbackConfig) => {
      const { contentId, customData } = loadRequest.media;
      this.logger.setup(customData?.logging || {});

      this.logger.info('loadRequest', loadRequest);
      this.streamStarted = false;

      const { video, currentTrack, uid, sendAdUpdates } = (customData || {}) as {
        video?: { source: Source; tracks: Track[]; metadata: BasePlayer['videoMetadata']; isPvx: boolean };
        currentTrack?: string;
        uid?: string;
        sendAdUpdates?: boolean;
        initiateAuth?: boolean;
      };
      if (currentTrack) this.currentTrack = currentTrack;
      if (sendAdUpdates) this.sendAdUpdates = sendAdUpdates;
      if (uid) this.uid = uid;

      this.contentId = contentId;
      this.textTracks = video?.tracks || [];

      // Setup DRM
      if (video?.source.drm?.widevine) {
        const widevine = video.source.drm.widevine;

        playbackConfig.protectionSystem = cast.framework.ContentProtection.WIDEVINE;
        playbackConfig.licenseUrl = widevine.url;

        playbackConfig.licenseRequestHandler = async (request) => {
          const token = video.isPvx
            ? await getPvxDrmToken({
                accessToken: this.getDeviceAccessToken(),
                mediaId: contentId,
                playSessionId: getPlaySessionId(contentId),
              })
            : await getToken(this.videoApiURL);

          request.headers['X-VUDRM-TOKEN'] = token;
        };
      }

      return playbackConfig;
    });

    this.playerManager.addEventListener(cast.framework.events.EventType.PLAYER_LOAD_COMPLETE, () => {
      this.currentDuration = this.playerManager.getDurationSec();

      customInteraction('Player Ready');
    });

    this.playerManager.addEventListener(cast.framework.events.EventType.CLIP_STARTED, (event) => {
      this.logger.debug(cast.framework.events.EventType.CLIP_STARTED, { adPlaying: this.adPlaying, event });
      if (this.adPlaying === false) {
        this.resetTracks();
        if (this.currentTrack) {
          this.setActiveCaption(this.currentTrack);
        }
      }
    });

    this.playerManager.addEventListener(cast.framework.events.EventType.BREAK_ENDED, () => {
      if (this.sendAdUpdates) {
        const breakManager = this.playerManager.getBreakManager();
        const breaks = breakManager.getBreaks();
        this.sendCustomMessage({
          type: ACTIONS.ON_BREAK_CHANGED,
          value: false,
          breaks,
        });
      }
    });

    this.playerManager.setMessageInterceptor(cast.framework.messages.MessageType.EDIT_TRACKS_INFO, (request) => {
      this.logger.info('EDIT_TRACKS_INFO', request);
      const [activeTrackId] = request.activeTrackIds || [];

      const textTracksManager = this.playerManager.getTextTracksManager();
      const tracks = textTracksManager.getTracks();

      const target =
        request.enableTextTracks && activeTrackId
          ? tracks.find((track) => track.trackId === activeTrackId)
          : { trackContentId: 'off', name: 'Off' };

      if (target) {
        this.currentTrack = target.trackContentId;
      }

      if (target) {
        this.sendCustomMessage({
          type: ACTIONS.TRACK_CHANGED,
          name: target.name,
          id: target.trackContentId,
        });
      }
      return request;
    });

    this.playerManager.addEventListener(cast.framework.events.EventType.TRACKS_CHANGED, (event) => {
      this.logger.info('TRACKS_CHANGED', event);
      const textTracksManager = this.playerManager.getTextTracksManager();
      const [track] = textTracksManager.getActiveTracks();
      if (track) this.sendCustomMessage({ type: ACTIONS.TRACK_CHANGED, value: track.name, source: 'eventListener' });
    });

    this.playerManager.addEventListener(cast.framework.events.EventType.ERROR, (event) => {
      const { error, detailedErrorCode, reason } = event;
      const customAttributes: { [key: string]: any } = {
        error_code: detailedErrorCode,
        error_group: detailedErrorCode && DETAILED_ERROR_CODES[detailedErrorCode],
        adPlaying: this.adPlaying || !!error?.message?.includes('Failed to request ads'),
      };

      let errorMessage = error?.message ?? `Error ${detailedErrorCode}`;

      if (error?.shakaErrorCode) {
        const { shakaErrorCode, shakaErrorData } = error;
        const [shakaError] = shakaErrorData || [];

        const [url, status, response] = shakaError?.data || [];

        errorMessage = `Shaka Error ${shakaErrorCode}`;

        this.failedShaka = true;
        this.failedShakaCode = shakaErrorCode;

        Object.assign(customAttributes, {
          shaka_error_code: shakaErrorCode,
          shaka_url: url,
          shaka_status: status,
          shaka_response: response,
        });
      }

      // Mark ad as failed when BREAK_CLIP_LOADING_ERROR (901) occurs to trigger playback restart without ads
      if (detailedErrorCode === 901) {
        this.failedAd = true;
        this.failedAdReason = 'BREAK_CLIP_LOADING_ERROR';
      }
      this.logger.error(error, reason, customAttributes);
      noticeError(error instanceof Error ? error : new Error(errorMessage), customAttributes);
    });
  }

  resetTracks(): void {
    const textTracksManager = this.playerManager.getTextTracksManager();
    const tracks = textTracksManager.getTracks();
    if (!tracks.length && this.textTracks.length) {
      this.logger.info('reset missing tracks');

      const textTracks = this.textTracks.map((track) => {
        const newTextTrack = this.textTracksManager.createTrack();
        newTextTrack.language = 'nl';
        newTextTrack.name = track.label;
        newTextTrack.trackContentId = track.file;
        newTextTrack.trackContentType = 'text/vtt’';
        newTextTrack.type = cast.framework.messages.TrackType.TEXT;
        return newTextTrack;
      });
      this.logger.info('new tracks:', textTracks);
      textTracksManager.addTracks(textTracks);
    }
  }

  setActiveCaption(id: string): void {
    const textTracksManager = this.playerManager.getTextTracksManager();
    this.logger.info('setActiveCaption', id);
    if (id === 'off') {
      this.logger.info('Disable caption');
      this.sendCustomMessage({ type: ACTIONS.ACTIVE_TRACK_SET, id, name: 'Off' });
      return textTracksManager.setActiveByIds([]);
    }
    const tracks = textTracksManager.getTracks();
    const target = tracks.find((track) => track.trackContentId === id || track.name === id);
    if (target) {
      this.logger.info(`set track '${target.trackId}' to active`, target);
      this.sendCustomMessage({ type: ACTIONS.ACTIVE_TRACK_SET, id: target.trackContentId, name: target.name });

      textTracksManager.setActiveByIds([target.trackId]);
    }
  }

  sendProgressUpdate(currentMediaTime: number): void {
    if (this.currentMediaTime && this.currentMediaTime === currentMediaTime) return;
    this.currentMediaTime = currentMediaTime;

    // Check if ad is currently being played
    // Heartbeat is not sent while ad is playing
    const breakClipTime = this.playerManager.getBreakClipCurrentTimeSec();
    if (breakClipTime != null) return;

    const isComplete = isVideoCompleted(currentMediaTime, this.currentDuration);
    this.logger.debug('sendProgressUpdate', {
      uid: this.uid,
      contentId: this.contentId,
      position: currentMediaTime,
      isComplete,
      videoApiURL: this.videoApiURL,
    });
    fetch(`${this.videoApiURL}/vpupdater?uid=${this.uid}&iid=${this.contentId}&prg=${currentMediaTime}&c=${isComplete}`)
      .then((response) => response.json())
      .then((result) => this.logger.debug('sendProgressUpdate result', result))
      .catch((error) => this.logger.error('sendProgressUpdate error', error));
  }

  /**
   * Sends messages to all connected sender apps.
   * @param {string} message Message to be sent to senders.
   * @private
   */
  broadcast(message: any) {
    this.context.sendCustomMessage(NAMESPACE_TWC, undefined, message);
  }

  sendCustomMessage(message: any) {
    this.context.sendCustomMessage(NAMESPACE_TALPA, undefined, JSON.stringify(message));
  }

  /**
   * Seeks content video.
   * @param {!float} time time to seek to.
   * @private
   */
  seek_(time: number) {
    this.currentContentTime = time;
    this.playerManager.seek(time);
    if (this.playerManager.getPlayerState() === cast.framework.messages.PlayerState.PAUSED) {
      this.playerManager.play();
    }
  }

  private startDeviceAuth(uid?: string): Promise<void> {
    if (this.authPromise && this.authenticatedUid === uid) return this.authPromise;

    if (this.deviceAuthManager) {
      this.deviceAuthManager.cleanup();
    }

    this.authenticatedUid = uid;

    this.showAuthOverlay();

    this.authPromise = new Promise((resolve, reject) => {
      this.deviceAuthManager = new DeviceAuthManager();
      this.deviceAuthManager.setCallbacks(
        (userCode, deviceEndpoint) => {
          this.sendCustomMessage({
            type: ACTIONS.DEVICE_AUTH_USER_CODE,
            userCode,
            deviceEndpoint,
          });
        },
        () => {
          this.sendCustomMessage({
            type: ACTIONS.DEVICE_AUTH_SUCCESS,
          });
          this.hideAuthOverlay();
          resolve();
        },
        (error) => {
          this.authenticatedUid = undefined;
          this.sendCustomMessage({
            type: ACTIONS.DEVICE_AUTH_ERROR,
            error,
          });
          this.hideAuthOverlay();
          noticeError(new Error(error));
          resolve();
        },
      );

      this.deviceAuthManager.startAuthFlow();
    });

    return this.authPromise;
  }

  private showAuthOverlay(): void {
    const overlay = document.getElementById('auth-overlay');
    if (overlay) overlay.classList.add('active');
  }

  private hideAuthOverlay(): void {
    const overlay = document.getElementById('auth-overlay');
    if (overlay) {
      overlay.classList.remove('active');
      overlay.style.display = 'none';
    }
  }

  getDeviceAccessToken(): string | null {
    return this.deviceAuthManager?.getAccessToken() || null;
  }
}
